import * as React from 'react';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import * as moment from 'moment';

import { ContextService } from 'src/core/services/context.service';
import { DataLocalService } from 'src/core/services/data-local.service';
import { IdentityService } from 'src/core/services/identity.service';
import { ToastNotifications } from 'src/core/ui/toast';
import { QuoteEntryView } from './quote-entry.view';
import { QuoteEntryDataService } from './quote-entry-form.data';
import { ErratasService } from 'src/app/bookings/shared/erratas.service';
import { TransportCompanyOfficeListDataService } from 'src/app/products/office/office.data';
import { StatusTexts } from "src/app/shared/status-texts";

interface QuoteEntryControllerProps extends ComponentViewProps<QuoteItem> {
	dataService: QuoteEntryDataService;
	taxes: TaxSummary[];
	paxs: QuotePaxItem[];
	mode: ControlMode;
	contextService: ContextService;
	formatService: any;
	reloadQuote: () => Promise<void>;
	rowCss: string;
	getOccupancy: (entry: QuoteEntryItem) => PersonOccupancy[];
	getSearchParameter: (entry: QuoteEntryItem, occupancies: Occupancy[]) => Search;
	updateCommissions: () => Promise<void>;
	updateDependantQuoteEntries: (quoteEntries?: QuoteEntryItem[]) => Promise<void>;
	updatePrice: (quoteEntries: QuoteEntryItem[]) => Promise<boolean>;
	updateQuote: (items?: QuoteEntryItem[]) => void;
	erratas: ErratasQuoteEntryItem[];
	erratasService: ErratasService;
	erratasConfirmed: ErratasQuoteEntryItemConfirmed[];
	currencyCode: string;
	autoconnetProductsConfig: { products: AutoConnetProductConfig[] }
	useAllotmentBeforeBookings: boolean;
}

@observer
export class QuoteEntryController extends React.Component<QuoteEntryControllerProps, {}> {
	@resolve
	identityService: IdentityService;

	@resolve
	toast: ToastNotifications;

	@observable
	erratas: ErratasQuoteEntryItem[];

	@observable
	historyDataService: DataLocalService<QuoteEntryStateHistory>;

	officeDataService: TransportCompanyOfficeListDataService;

	private addProduct = async (products: ResultSelection[], search: Search, taxId?: number): Promise<QuoteEntryItem[]> => {
		this.props.setBusy(true);
		let productFilter = products.filter(p => p.selected.result.allotmentAvailable == null || (p.selected.result.allotmentAvailable && (p.selected.result.allotmentAvailable.includes('Stopsale') == false && p.selected.result.allotmentAvailable.includes('Unavailable') == false)));
		const items = await this.props.dataService.add(this.props.model, productFilter, search, null, taxId);
		await this.props.updateCommissions();
		this.props.updateQuote();
		for (const entry of items) {
			if (entry.dynamicProperties.allotmentId && entry.dynamicProperties.allotmentId > 0 && this.props.useAllotmentBeforeBookings && entry.dynamicProperties.allotmentAvailable.includes('Byavailability') == true) {
				let allotmentId = entry.dynamicProperties.allotmentId;
				let date = moment.utc();
				let expirationDate = date.add(30, 'minutes')
				let idAllotmentRequest = await this.block(1, allotmentId, entry.dateFrom, entry.dateTo, expirationDate.toDate());
				entry.allotmentRequestId = idAllotmentRequest > 0 ? idAllotmentRequest : undefined;
			}
			if (entry.productType == 2) {
				if (entry.dynamicProperties.driver == null) {
					let pax = this.props.paxs.find(e => e.clientId === this.props.model.clientId);
					if (pax != null)
						entry.dynamicProperties.driver = pax.id;
				}
			}
		}
		await this.setErratas(items);
		this.props.setBusy(false);

		return items;
	}

	@autobind
	private async block(qty: number, allotmentId: number, dateFrom: Date, dateTo: Date, expirationDate: Date) {
		this.props.setBusy(true);
		return this.props.contextService.current().then(ctx =>
			ctx.Block(allotmentId, dateFrom, dateTo, qty, expirationDate)
				.then(res => {
					this.props.setBusy(false);
					return res;
				}).catch(err => { return undefined })
		);

	}
	private deleteProducts = async (items: QuoteEntryItem[]): Promise<void> => {
		this.props.setBusy(true);

		for (let entry of items) {
			let index = this.props.erratas.findIndex(x => x.productId == entry.productId && x.supplierId == entry.supplierId);
			if (index != -1)
				this.props.erratas.splice(index, 1);
		}
		await this.props.updateCommissions();
		this.props.updateQuote();

		this.props.setBusy(false);
	}

	private restoreProducts = async (items: QuoteEntryItem[]): Promise<void> => {
		this.props.setBusy(true);
		//await this.props.updateFees();
		const deprecatedFees = this.props.dataService.items
			.filter(m => m.dynamicProperties['isProductFee']);
		let restoreFees: QuoteEntryItem[] = [];
		for (let entry of items) {
			const productFee = deprecatedFees.find(m => entry.id === m.parentEntryId);
			if (productFee ) {
				restoreFees.push(productFee);
			}
		}
		this.props.dataService.restore(restoreFees);
		
		await this.props.updateCommissions();
		this.props.updateQuote(items);
		await this.setErratas(items);

		this.props.setBusy(false);
	}

	private async setErratas(items: QuoteEntryItem[]) {
		let results = await this.props.erratasService.getErratasForAllQuoteEntries({
			bookingDate: this.props.model.reservationDate,
			erratasParameter: items.map(item => ({
				productId: item.productId,
				serviceDateFrom: item.dateFrom,
				serviceDateTo: item.dateTo,
				supplierId: item.supplierId
			}))
		});
		for (const item of items) {
			for (const result of results) {
				if (item.productId === result.productId && item.supplierId === result.supplierId) {
					let errataduplicate = this.props.erratas.find(x => x.productId == result.productId && x.supplierId == result.supplierId);
					if (errataduplicate == null) {
						this.props.erratas.push(result);
						if (result.erratas && result.erratas.length > 0) {
							for (let errata of result.erratas) {
								this.props.erratasConfirmed.push({
									quoteEntryId: item.id,
									confirmed: false,
									errata: errata,
								});
							}
						}
					}
				}
			}
		}
	}

	@autobind
	private async changeStatus(entry: QuoteEntryItem, observations: string, confirmationNumber: string, rejectedAs?: number) {
		const ctx = await this.props.contextService.current();
		const newStatus = await ctx.QuoteEntryChangeStatus(entry.id, observations, confirmationNumber, rejectedAs);
		let newStatusText=StatusTexts.getQuoteEntryStatusFromNumber(newStatus)
		this.toast.showToast('Request succeed!', `Product status changed to ${newStatusText}`, 'success');
		if (this.props.dataService.items.every(e => !e.isDeleted && (e.id != entry.id ? e.status === 3 : confirmationNumber != null))) {
			this.props.reloadQuote();
		} else {
			this.props.dataService.reload();
		}
	}

	loadStateHistory = (entry: QuoteEntryItem) => {
		const history: QuoteEntryStateHistory[] = entry.dynamicProperties['history'] || [];
		this.historyDataService = new DataLocalService(history);
	}

	@autobind
	checkProductDelete(items: QuoteEntryItem[]) {
		if (this.props.model.status != 0 && items.some(i => i.status > 0 && i.status < 4 && i.entity.entityState != 20)) {
			this.toast.showToast('Invalid operation!', "Products with state 'Draft', 'Cancelled' or 'Denied' are the only ones that can be deleted, you can 'Cancel' other products", 'error');
			return null;
		}
		const toDelete: QuoteEntryItem[] = [];
		let feesInList = false;
		for (const item of items) {
			if (item.dynamicProperties.isFee) {
				feesInList = true;
				continue;
			}
			toDelete.push(item);
			const subItems = this.props.dataService.items.filter(e => e.parentEntryId === item.id && (e.productType === 6 || e.productType === 7));
			toDelete.splice(toDelete.length - 1, 0, ...subItems);
			let confirmed = this.props.erratasConfirmed.filter(x => x.quoteEntryId == item.id);
			for (let confirm of confirmed) {
				let indexConfirmed = this.props.erratasConfirmed.findIndex(x => x == confirm);
				if (indexConfirmed != -1)
					this.props.erratasConfirmed.splice(indexConfirmed, 1);
			}
		}
		if (feesInList) {
			this.toast.showToast('Invalid operation!', "Automatic fees cannot be deleted", 'info');
		}
		return toDelete;
	}


	@autobind
	async addProductManually(product: ProductManually): Promise<QuoteEntryItem[]> {
		this.props.setBusy(true);
		let salesAdjustment: QuoteEntryItem;
		let item: QuoteEntryItem;
		if (product.productType === 7) {
			salesAdjustment = this.props.dataService.items.find(e => e.productType === 7);
		}
		if (salesAdjustment) {
			salesAdjustment.sellPrice = product.sellPrice;
			salesAdjustment.dynamicProperties.sellPriceResult = product.sellPriceResult;
		} else {
			item = await this.props.dataService.addProductManually(this.props.model, product);
		}
		await this.props.updateCommissions();
		this.props.updateQuote();
		this.props.setBusy(false);

		return [item];
	}

	handleEditQuoteEntryField = async (entries: QuoteEntryItem[], requireUpdatePrice: boolean = false, requirePolicyChecking: boolean = false): Promise<void> => {
		const { model, updateDependantQuoteEntries, updatePrice } = this.props;

		if (requireUpdatePrice) {
			await updatePrice(entries);
		} else if (requirePolicyChecking) {
			await updateDependantQuoteEntries(entries);
		}
	};

	handleAdjustEntryPrice = async (entry: QuoteEntryItem, costPrice: number, sellPrice: number, salesmanProfit: number): Promise<void> => {
		const paxs = toJS(entry.dynamicProperties.occupancy).map(e => e.id);
		const saleAdjustment: SalesAdjustmentProductManually = {
			quoteId: entry.quoteId,
			productDisplayName: 'Price Adjustment',
			productType: 7, // SalesAdjustment
			quoteEntryId: entry.id,
			dateFrom: entry.dateFrom,
			dateTo: entry.dateTo,
			supplierId: entry.supplierId,
			supplierTitle: entry.supplierTitle,
			occupancies: entry.dynamicProperties.occupancy,
			requiresConfirmation: false,
			costPrice: costPrice,
			costPriceResult: {
				breakdown: [{
					exchangeRate: 1,
					percent: false,
					price: 0,
					priceType: 'PerUnit',
					unitPrice: 0,
				}],
				exchangeRate: 1,
				isCommissionable: false,
				isPriceLive: true,
				currencyIsoCode: entry.dynamicProperties.costPriceResult.currencyIsoCode,
				sourceCurrencyIsoCode: entry.dynamicProperties.costPriceResult.sourceCurrencyIsoCode,
				totalPrice: 0,
				paxs: paxs,
			},
			sellPrice: sellPrice,
			salePriceGross: sellPrice,
			sellPriceResult: {
				breakdown: [{
					exchangeRate: 1,
					percent: false,
					price: 0,
					priceType: 'PerUnit',
					unitPrice: 0,
				}],
				exchangeRate: 1,
				isCommissionable: false,
				isPriceLive: true,
				currencyIsoCode: entry.dynamicProperties.costPriceResult.currencyIsoCode,
				sourceCurrencyIsoCode: entry.dynamicProperties.costPriceResult.sourceCurrencyIsoCode,
				totalPrice: 0,
				paxs: paxs,
			},
			salesmanProfit: salesmanProfit,
			productCategory: undefined,
			productServiceType: undefined,
			isRelative: true
		};

		const { dataService, model, updateQuote, setBusy } = this.props;

		setBusy(true);
		try {
			await dataService.adjustEntryPrice(model, dataService.items, saleAdjustment, updateQuote);
		} catch (error) {
			this.toast.showToast('Error!', 'There was an error fixing the salesman profit', 'error');
		} finally {
			setBusy(false);
		}
	};

	render() {
		if (this.props.dataService == null || this.props.dataService.items == null || this.props.autoconnetProductsConfig == null) {
			return null;
		}

		return (
			<QuoteEntryView
				model={this.props.dataService}
				quote={this.props.model}
				taxes={this.props.taxes}
				paxs={this.props.paxs}
				addProduct={this.addProduct}
				deleteProducts={this.deleteProducts}
				restoreProducts={this.restoreProducts}
				checkProductDelete={this.checkProductDelete}
				formatService={this.props.formatService}
				identityService={this.identityService}
				mode={this.props.mode}
				loadStateHistory={this.loadStateHistory}
				historyDataService={this.historyDataService}
				changeStatus={this.changeStatus}
				setBusy={this.props.setBusy}
				rowCss={this.props.rowCss}
				getOccupancy={this.props.getOccupancy}
				getSearchParameter={this.props.getSearchParameter}
				addProductManually={this.addProductManually}
				onEditQuoteEntryField={this.handleEditQuoteEntryField}
				updateCommissions={this.props.updateCommissions}
				updateQuote={this.props.updateQuote}
				erratas={this.props.erratas}
				currencyCode={this.props.currencyCode}
				autoconnetProductsConfig={this.props.autoconnetProductsConfig}
				onPinUpSalesmanProfit={(entry) => this.handleAdjustEntryPrice(entry, 0, 0, entry.salesmanProfit)}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/quote-entries/quote-entry.controller.tsx